/**
 * Planos de saúde — convênio, cartão de desconto e particular (trilha T5a).
 *
 * No banco a tabela se chama `convenios`: "plano" já é o plano de ASSINATURA
 * da plataforma, e nome ambíguo é bug esperando a hora — a mesma razão do
 * `planosData.jsx` do protótipo.
 */

export type TipoDePlano = 'Convênio' | 'Cartão de desconto' | 'Particular';

export const TIPOS_DE_PLANO: TipoDePlano[] = ['Convênio', 'Cartão de desconto', 'Particular'];

export const CARENCIAS: { valor: string; rotulo: string }[] = [
    { valor: '', rotulo: 'Sem carência' },
    { valor: '30 dias', rotulo: '30 dias' },
    { valor: '90 dias', rotulo: '90 dias' },
    { valor: '120 dias', rotulo: '120 dias' },
    { valor: '180 dias', rotulo: '180 dias' },
];

export const TABELAS_DE_PRECO = ['Tabela da clínica', 'Tabela da clínica −10%', 'CBHPM 2024', 'CBHPM 2022', 'Própria da operadora'];

export const PRAZOS_DE_RECEBIMENTO = ['Imediato', '30 dias', '45 dias', '60 dias', '90 dias'];

export interface DadosDoPlanoDeSaude {
    nome: string;
    tipo: TipoDePlano;
    operadora?: string;
    /** Seis dígitos, sem pontuação. Só convênio tem. */
    registroAns?: string;
    /** Só convênio tem. */
    carencia?: string;
    tabelaDePrecos?: string;
    /** Percentual do valor de tabela que a clínica recebe, de 0 a 100. */
    repassePercentual?: number;
    prazoRecebimento?: string;
    cor: string;
    observacoes?: string;
    /** Inativo sai das opções da ficha do paciente e continua no cadastro. */
    ativo: boolean;
}

export interface PlanoDeSaude extends DadosDoPlanoDeSaude {
    id: string;
    /** Pacientes com este plano gravado na ficha. */
    pacientes: number;
}

/** As mesmas regras das restrições da `0085`, ditas antes de ir ao banco. */
export function problemaDoPlano(d: DadosDoPlanoDeSaude): string | null {
    if (!d.nome.trim()) return 'Dê um nome ao plano.';
    if (!TIPOS_DE_PLANO.includes(d.tipo)) return 'Tipo de plano inválido.';
    const registro = d.registroAns?.trim();
    if (d.tipo === 'Convênio' && registro && !/^\d{6}$/.test(registro)) return 'O registro na ANS tem seis dígitos, sem pontuação.';
    if (d.repassePercentual !== undefined && !(d.repassePercentual >= 0 && d.repassePercentual <= 100)) return 'O repasse vai de 0 a 100%.';
    if (!/^#[0-9A-Fa-f]{6}$/.test(d.cor)) return 'Escolha a cor do plano.';
    return null;
}

/**
 * Tira os campos vazios, e os de convênio de quem não é convênio: cartão de
 * desconto com "carência de 90 dias" guardada seria regra que ninguém aplica.
 */
export function normalizarPlano(d: DadosDoPlanoDeSaude): DadosDoPlanoDeSaude {
    const texto = (v?: string) => (v?.trim() ? v.trim() : undefined);
    const convenio = d.tipo === 'Convênio';
    const operadora = texto(d.operadora);
    const registroAns = convenio ? texto(d.registroAns) : undefined;
    const carencia = convenio ? texto(d.carencia) : undefined;
    const tabelaDePrecos = texto(d.tabelaDePrecos);
    const prazoRecebimento = texto(d.prazoRecebimento);
    const observacoes = texto(d.observacoes);
    return {
        nome: d.nome.trim(),
        tipo: d.tipo,
        cor: d.cor,
        ativo: d.ativo,
        ...(operadora ? { operadora } : {}),
        ...(registroAns ? { registroAns } : {}),
        ...(carencia ? { carencia } : {}),
        ...(tabelaDePrecos ? { tabelaDePrecos } : {}),
        ...(d.repassePercentual !== undefined ? { repassePercentual: d.repassePercentual } : {}),
        ...(prazoRecebimento ? { prazoRecebimento } : {}),
        ...(observacoes ? { observacoes } : {}),
    };
}
